import { createContext, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { URole } from '../constants/navigation';
import useAPI from '../hooks/useAPI';
import type { IUser } from '../types/UserType';
import { getCookies, removeCookies, setCookies } from '../utility/cookies';

interface AuthContextType {
  accessToken?: string;
  userRole: string;
  setUserRole: (role: string) => void;
  userData: IUser | null;
  setUserData: (data: IUser | null) => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  adminBalance: IAdminBalance | undefined;
  refetchAdminBalance: () => void;
  isAdminBalanceLoading: boolean;
  setAdminBalance: (value: any) => void;
  resetData: () => void;
  refetchUserData: () => void;
  setAccessToken: (val?: string) => void;
  updateUserProfile: (data: {
    image?: string;
    firstName?: string;
    lastName?: string;
  }) => void;
}

interface IAdminBalance {
  UserBalance: number;
  LastMintToken: string;
  lastTransferTokens: number;
  totalBalance: number;
  totalMint: number;
  totalBurn: number;
  totalTransfer: number;
  totalReceive: number;
  message: string;
  pendingWithdrawCount: number;
  totalUserCount: number;
  successWithdrawCount: number;
}

const AuthContext = createContext<AuthContextType>({
  accessToken: '',
  userRole: '',
  setUserRole: () => {},
  userData: null,
  setUserData: () => {},
  isAuthenticated: false,
  isLoading: false,
  adminBalance: undefined,
  isAdminBalanceLoading: false,
  refetchAdminBalance: () => {},
  setAdminBalance: () => {},
  resetData: () => {},
  updateUserProfile: () => {},
  refetchUserData: () => {},
  setAccessToken: () => {},
});

export default AuthContext;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const token = getCookies('accessToken');
  const [userRole, setUserRole] = useState<string>('');
  const [userData, setUserData] = useState<IUser | null>(null);
  const [adminBalance, setAdminBalance] = useState<IAdminBalance | undefined>(
    undefined,
  );
  const [accessToken, setAccessToken] = useState(token || '');

  const { isLoading, refetch: refetchUserData } = useAPI({
    url: '/auth/get/info',
    method: 'GET',
    autoFetch: !!token,
    dependencies: [token],
    enabled: !!accessToken,
    onSuccess: data => {
      // console.log('🚀 ~ AuthProvider ~ data:', data);
      setUserData(data?.result?.data);
      setUserRole(data?.result?.data?.role);
      setCookies('role', data?.result?.data?.role);
    },
    onError: error => {
      toast.error(error?.response?.data?.message || 'An error occurred');
    },
  });

  const {
    data: adminBalanceData,
    refetch: refetchAdminBalance,
    isLoading: isAdminBalanceLoading,
  } = useAPI({
    url: `/token/reduce/user`,
    method: 'GET',
    autoFetch: false,
    enabled: userData?.role === URole.COMPANY_ADMIN,
    dependencies: [userRole],
    onSuccess: data => {
      setAdminBalance(data?.result);
    },
    onError: error => {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch admin balance',
      );
    },
  });

  const resetData = () => {
    setUserRole('');
    setUserData(null);
    setAdminBalance(undefined);
    removeCookies('accessToken');
    removeCookies('refreshToken');
    removeCookies('user-shard');
    setAccessToken('');
    localStorage.clear();
  };

  const updateUserProfile = (data: {
    image?: string;
    firstName?: string;
    lastName?: string;
  }) => {
    setUserData(prevData => {
      if (prevData) {
        return {
          ...prevData,
          ...data,
        };
      }
      return null;
    });
  };

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        userRole,
        setUserRole,
        userData,
        setUserData,
        isAuthenticated: !!userData,
        isLoading,
        adminBalance,
        refetchAdminBalance,
        isAdminBalanceLoading,
        setAdminBalance,
        resetData,
        updateUserProfile,
        refetchUserData,
        setAccessToken,
      }}
    >
      {isLoading ? (
        <div className="vh-100 w-100 d-flex justify-content-center align-items-center">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
